// ========================================
// LAYOUT COMPONENT - Tenant-Aware
// Displays tenant branding (logo, colors, tagline)
// frontend/src/components/Layout.js
// ========================================

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import api from '../services/api';
import BrandingModal from './BrandingModal';

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { tenant, loading, getLabel, isResidential } = useTenant();
  const [showSettings, setShowSettings] = useState(false);
  const [showBrandingModal, setShowBrandingModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/api/notifications?limit=10');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, []);

  // Fetch unread count only (for polling)
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await api.get('/api/notifications/unread-count');
      setUnreadCount(response.data.count || 0);
    } catch (err) {
      // Silently fail for background polling
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchNotifications();
    // Poll for unread count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchUnreadCount]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/api/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read_at: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await api.post('/api/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Get time ago string
  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'hot_lead':
        return '🔥';
      case 'new_lead':
        return '📥';
      case 'return_visitor':
        return '🔄';
      default:
        return '🔔';
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Role helpers
  const isAdmin = ['client_admin', 'super_admin', 'svp'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const canEdit = ['client_admin', 'super_admin', 'svp', 'manager', 'sales'].includes(user?.role);
  
  // Get role display name
  const getRoleDisplay = (role) => {
    const names = {
      'super_admin': 'Super Admin',
      'client_admin': 'Admin',
      'svp': 'Admin',
      'manager': 'Manager',
      'sales': 'Manager',
      'team_member': 'Team Member',
      'viewer': 'Viewer',
      'consultant': 'Viewer'
    };
    return names[role] || role;
  };

  // Dynamic navigation based on industry and role
  // Removed Regions from main nav - now in Settings dropdown
  const navigation = [
    { name: 'Dashboard', path: '/dashboard', icon: 'home' },
    { name: getLabel('asset'), path: '/assets', icon: 'building' },
    { name: 'Clients', path: '/clients', icon: 'users' },
    { name: 'Leasing Rooms', path: '/plrs', residential: 'Sales Rooms', icon: 'presentation' },
    { name: 'Analytics', path: '/analytics', icon: 'chart' },
    { name: 'Campaigns', path: '/campaigns', icon: 'mail' },
    ...(tenant?.products?.agent ? [{ name: 'Agent', path: '/agent/conversations', icon: 'bot' }] : []),
    ...((user?.role === 'super_admin' || tenant?.products?.studio?.plan || tenant?.products?.studio?.preview_user_ids?.includes(user?.userId || user?.user_id)) ? [{ name: 'Studio', path: '/studio', icon: 'presentation' }] : []),
  ].map(item => ({
    ...item,
    name: isResidential && item.residential ? item.residential : item.name
  }));

  // Check if current path matches nav item
  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    if (path === '/analytics') return location.pathname === '/analytics' || location.pathname.includes('/analytics-enhanced');
    if (path === '/agent/conversations') return location.pathname.startsWith('/agent');
    return location.pathname.startsWith(path);
  };

  // Primary color for styling (with fallback)
  const primaryColor = tenant?.primary_color || '#111827';
  const tagline = tenant?.settings?.tagline || tenant?.tagline || 'Thinking Ahead';

  // Build logo URL
  const getLogoUrl = () => {
    if (!tenant?.logo_url) return null;
    const url = tenant.logo_url;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/gallery/') || url.includes('gallery-')) {
      const baseUrl = process.env.REACT_APP_API_URL || '';
      return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/api/gallery/${url}`;
    }
    return url;
  };

  const logoUrl = getLogoUrl();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with tenant branding */}
      <header 
        className="bg-white border-b border-gray-200"
        style={{ borderBottomColor: primaryColor + '20' }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            {/* Tenant Logo */}
            <Link to="/dashboard" className="flex items-center space-x-3">
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={tenant?.name || 'Logo'}
                  className="h-10 max-w-[200px] object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <span 
                  className="text-xl font-bold"
                  style={{ color: primaryColor }}
                >
                  {tenant?.name || 'Archiverse'}
                </span>
              )}
            </Link>

            {/* Navigation */}
            <nav className="hidden lg:flex space-x-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
                    isActive(item.path)
                      ? 'text-white'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  style={isActive(item.path) ? { 
                    backgroundColor: primaryColor,
                    color: 'white'
                  } : {}}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* User menu + Settings */}
            <div className="flex items-center space-x-3">
              {/* Settings dropdown for admins */}
              {isAdmin && (
                <div className="relative">
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>

                  {/* Settings Dropdown */}
                  {showSettings && (
                    <>
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setShowSettings(false)}
                      />
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-20">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Settings</p>
                        </div>
                        
                        {/* Team Members */}
                        <Link
                          to="/users"
                          onClick={() => setShowSettings(false)}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        >
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          <span>Team Members</span>
                        </Link>

                        {/* PII Access Log — only for tenants with PII masking on */}
                        {tenant?.products?.mask_pii && (
                          <Link
                            to="/pii-access-log"
                            onClick={() => setShowSettings(false)}
                            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                          >
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span>PII Access Log</span>
                          </Link>
                        )}

                        {/* Regions - Moved here from main nav */}
                        <Link
                          to="/regions"
                          onClick={() => setShowSettings(false)}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        >
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                          </svg>
                          <span>Regions</span>
                        </Link>

                        {/* Integrations (the Meta Connect-Facebook card) — shown to
                            client-admins (incl. the Meta reviewer). Hidden from
                            super_admin, who gets the unified "Connectors" directory
                            below instead, so each role has ONE integrations door. */}
                        {!isSuperAdmin && (
                          <Link
                            to="/settings/integrations"
                            onClick={() => setShowSettings(false)}
                            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                          >
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            <span>Integrations</span>
                          </Link>
                        )}

                        {/* Connectors (P3d directory) — super_admin ONLY. The single
                            integrations home for super-admins: covers lead sources
                            AND CRM destinations (reached via its drawers). Hidden from
                            the Meta reviewer. Becomes THE Integrations page for all
                            admins after leads_retrieval approval. */}
                        {isSuperAdmin && (
                          <Link
                            to="/settings/connectors"
                            onClick={() => setShowSettings(false)}
                            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                          >
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                            </svg>
                            <span>Connectors</span>
                            <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-200">Preview</span>
                          </Link>
                        )}

                        {/* Logo & Favicon */}
                        <button
                          onClick={() => {
                            setShowBrandingModal(true);
                            setShowSettings(false);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        >
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>Logo &amp; Favicon</span>
                        </button>

                        {/* Super Admin Link */}
                        {isSuperAdmin && (
                          <Link
                            to="/admin"
                            onClick={() => setShowSettings(false)}
                            className="w-full px-4 py-2.5 text-left text-sm text-purple-700 hover:bg-purple-50 flex items-center gap-3"
                          >
                            <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            <span>Super Admin</span>
                          </Link>
                        )}

                        {/* CRM Integrations link removed — it now lives inside the
                            Connectors directory's Destinations section (reached via
                            the CRM tile → "Configure in CRM Integrations"). The page
                            itself remains at /admin/crm-integrations. */}

                        {/* Help / Documentation */}
                        <a
                          href="https://docs.archiverse.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                          onClick={() => setShowSettings(false)}
                        >
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Help & Docs</span>
                        </a>

                        {/* Notification Preferences */}
                        <Link
                          to="/notification-preferences"
                          onClick={() => setShowSettings(false)}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                        >
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                          </svg>
                          <span>Notification Preferences</span>
                        </Link>

                        <div className="border-t border-gray-100 mt-2 pt-2">
                          <div className="px-4 py-2 text-xs text-gray-400">
                            Archiverse v1.0.0
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Notification Bell */}
              <div className="relative">
                <button
                  onClick={() => {
                    // Refresh the list (not just the polled count) when opening,
                    // so a lead that arrived since mount shows without a page reload.
                    if (!showNotifications) fetchNotifications();
                    setShowNotifications(!showNotifications);
                    setShowSettings(false);
                    setShowUserMenu(false);
                  }}
                  className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {showNotifications && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowNotifications(false)}
                    />
                    <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg border border-gray-200 z-20 max-h-96 overflow-hidden">
                      {/* Header */}
                      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900">Notifications</h3>
                        {unreadCount > 0 && (
                          <button
                            onClick={markAllAsRead}
                            className="text-xs text-blue-600 hover:text-blue-700"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>
                      
                      {/* Notification List */}
                      <div className="max-h-72 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="px-4 py-8 text-center text-gray-500">
                            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                            </svg>
                            <p className="text-sm">No notifications yet</p>
                          </div>
                        ) : (
                          notifications.map((notification) => (
                            <div
                              key={notification.notification_id}
                              onClick={() => {
                                if (!notification.read_at) markAsRead(notification.notification_id);
                                setShowNotifications(false);
                                if (notification.client_id) navigate(`/clients/${notification.client_id}`);
                                else if (notification.plr_id) navigate('/clients');
                              }}
                              className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                                notification.client_id || notification.plr_id ? 'cursor-pointer' : ''
                              } ${
                                !notification.read_at ? 'bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <span className="text-lg flex-shrink-0">
                                  {getNotificationIcon(notification.notification_type)}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm ${!notification.read_at ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                                    {notification.title}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                                    {notification.plr_title}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {getTimeAgo(notification.created_at)}
                                    {notification.plr_creator_name && notification.plr_creator_id !== user?.user_id && (
                                      <span className="ml-2">• by {notification.plr_creator_name}</span>
                                    )}
                                  </p>
                                </div>
                                {!notification.read_at && (
                                  <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      
                      {/* Footer */}
                      <div className="px-4 py-2 border-t border-gray-100">
                        <Link
                          to="/notifications"
                          onClick={() => setShowNotifications(false)}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          View all notifications →
                        </Link>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* User info and menu */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 text-left hover:bg-gray-100 rounded-lg px-2 py-1 transition-colors"
                >
                  <div className="text-right hidden lg:block">
                    <div className="text-sm font-medium text-gray-900">{user?.name}</div>
                    <div className="text-xs text-gray-500">
                      {getRoleDisplay(user?.role)}
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium">
                    {user?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                </button>
                
                {/* User dropdown */}
                {showUserMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-20">
                      <div className="px-4 py-2 border-b border-gray-100 lg:hidden">
                        <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                        <p className="text-xs text-gray-500">{getRoleDisplay(user?.role)}</p>
                      </div>
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          handleLogout();
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
                      >
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="lg:hidden border-t border-gray-100">
          <div className="flex overflow-x-auto px-4 py-2 space-x-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg whitespace-nowrap ${
                  isActive(item.path)
                    ? 'text-white'
                    : 'text-gray-600 bg-gray-100'
                }`}
                style={isActive(item.path) ? { 
                  backgroundColor: primaryColor 
                } : {}}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>

        {/* Agent Sub-Navigation */}
        {location.pathname.startsWith('/agent') && tenant?.products?.agent && (
          <div className="border-t border-gray-100 bg-gray-50">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex space-x-1 py-1">
                {[
                  { name: 'Conversations', path: '/agent/conversations' },
                  { name: 'Projects', path: '/agent/projects' },
                  { name: 'Automations', path: '/agent/automations' },
                ].map((tab) => (
                  <Link
                    key={tab.path}
                    to={tab.path}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      location.pathname === tab.path
                        ? 'text-gray-900 bg-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {children}
      </main>

      {/* Footer with tenant branding */}
      <footer className="bg-white border-t border-gray-200 mt-20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex justify-between items-center text-sm text-gray-500">
            <p>© {new Date().getFullYear()} {tenant?.name || 'Archiverse'} · <span className="font-medium">propl</span> <span className="text-gray-400">— Know who's ready to buy</span></p>
            <p className="italic" style={{ color: primaryColor }}>
              {tagline}
            </p>
          </div>
        </div>
      </footer>

      {showBrandingModal && (
        <BrandingModal onClose={() => setShowBrandingModal(false)} />
      )}
    </div>
  );
}
